/**
 * Core-100 R5 (CORE-AQ) — Create Ticket FROM a call's appointment proposal.
 *
 * Constitution (CORE-AQ): "A conversation may propose an appointment; an advisor commits it, and no appointment may be
 * created from a conversation without a person accepting it." The proposal (`call_appointment_proposals`) is a DERIVED
 * extraction. This module turns an undecided, current proposal into Create Ticket PREFILL — every value marked as
 * derived from the call and editable — and the advisor submits through the NORMAL Create Ticket path. The ticket
 * carries `source_call_proposal_id`; the database (20270206025500) confirms the proposal against the new ticket in the
 * same transaction and refuses a stale / decided / other-shop proposal or a non-front-office creator.
 *
 * Rules held here (pure, no React):
 *   - nothing is invented: a value the call did not carry, or carried with LOW / UNKNOWN confidence, stays EMPTY with
 *     a note saying why;
 *   - the customer and vehicle are NOT matched here: they come from the call's own association (the server's
 *     identity rules recorded on `call_events.linked_customer_id / linked_vehicle_id`), and only when that link is a
 *     person's or a confident one; the page then selects them through its ordinary customer/vehicle selection;
 *   - a job is suggested only where the canonical catalog matcher (`matchExtractionToCatalog`) resolves the heard
 *     service — this module only decides WHAT to hand it;
 *   - the proposed day becomes the appointment day only when it is a real, not-past shop day; the heard window is a
 *     note (Create Ticket has no drop-off window field).
 */
import type { ExtractedConfidence, ExtractionProvenance } from '@/lib/intake/extractionProvenance';
import { conversationConfidenceTier } from '@/lib/intake/extractionProvenance';

/** The URL parameter that opens Create Ticket from a proposal. */
export const FROM_CALL_PROPOSAL_PARAM = 'fromCallProposal';
export const createTicketFromCallProposalPath = (proposalId: string) =>
  `/create-ticket?${FROM_CALL_PROPOSAL_PARAM}=${encodeURIComponent(proposalId)}`;

export interface CallProposalForTicket {
  id: string;
  shop_id: string;
  call_event_id: string;
  service_summary: string;
  proposed_date: string | null;
  proposed_window: string | null;
  vehicle_hint: string | null;
  confidence: number | null;
  status: 'proposed' | 'confirmed' | 'rejected';
  confirmed_ticket_id?: string | null;
  created_at: string;
}

export interface CallProposalCallLink {
  linked_customer_id: string | null;
  linked_vehicle_id: string | null;
  link_basis: 'inferred' | 'person' | null;
  routing_confidence: number | null;
}

export type CallProposalAvailability =
  | { kind: 'usable' }
  | { kind: 'not_found' }
  | { kind: 'decided'; status: 'confirmed' | 'rejected' }
  | { kind: 'stale' };

/** Whether this proposal may become a ticket (the server re-checks every one of these at creation). */
export function callProposalAvailability(
  proposal: CallProposalForTicket | null,
  latestProposalIdForCall: string | null,
): CallProposalAvailability {
  if (!proposal) return { kind: 'not_found' };
  if (proposal.status !== 'proposed') return { kind: 'decided', status: proposal.status };
  if (latestProposalIdForCall && latestProposalIdForCall !== proposal.id) return { kind: 'stale' };
  return { kind: 'usable' };
}

export function callProposalAvailabilityText(a: CallProposalAvailability): string | null {
  switch (a.kind) {
    case 'usable': return null;
    case 'not_found': return 'This call suggestion is not available in this shop. Nothing was filled in.';
    case 'decided': return a.status === 'confirmed'
      ? 'This call suggestion was already confirmed on a ticket. Nothing was filled in.'
      : 'This call suggestion was rejected. Nothing was filled in.';
    case 'stale': return 'A newer suggestion from the same call replaced this one. Nothing was filled in.';
  }
}

/** A call's customer link is used only when a person made it or the association was confident. */
export const CALL_LINK_MIN_CONFIDENCE = 0.8;

export interface CallProposalPrefill {
  proposalId: string;
  tier: ExtractedConfidence;
  /** What the caller asked for, for the concern field ('' when the extraction was not confident enough). */
  concern: string;
  /** What to hand the canonical catalog matcher ([] when not confident enough). */
  requestedServices: string[];
  /** yyyy-mm-dd: the appointment day, when usable. */
  appointmentDate: string | null;
  /** The call's linked customer / vehicle (the server's association), when usable. */
  customerId: string | null;
  vehicleId: string | null;
  /** Year / make / model heard on the call, only when no linked vehicle and the hint parses unambiguously. */
  vehicle: { year: number; make: string; model: string } | null;
  /** One line per field: what was filled from the call, or why it was left empty. */
  notes: Array<{ field: 'customer' | 'vehicle' | 'day' | 'window' | 'service'; filled: boolean; text: string }>;
}

const VEHICLE_HINT = /^\s*((?:19|20)\d{2})\s+([A-Za-z][A-Za-z-]*)\s+([A-Za-z0-9][A-Za-z0-9 .-]*?)\s*$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** "2019 Honda Civic" → { year, make, model }; anything else (or an impossible year) → null. */
export function parseVehicleHint(hint: string | null | undefined, maxYear: number): { year: number; make: string; model: string } | null {
  const m = (hint ?? '').match(VEHICLE_HINT);
  if (!m) return null;
  const year = Number(m[1]);
  if (!Number.isInteger(year) || year < 1950 || year > maxYear) return null;
  return { year, make: m[2], model: m[3] };
}

const TIER_WORDS: Record<ExtractedConfidence, string> = {
  high: 'high confidence', medium: 'medium confidence — check it', low: 'LOW confidence', unknown: 'confidence unknown',
};

export function buildCallProposalPrefill(
  proposal: CallProposalForTicket,
  call: CallProposalCallLink | null,
  opts: { todayInShop: string; maxVehicleYear: number },
): CallProposalPrefill {
  const tier = conversationConfidenceTier(proposal.confidence);
  const confident = tier === 'high' || tier === 'medium';
  const notes: CallProposalPrefill['notes'] = [];
  const tierText = TIER_WORDS[tier];

  // ---------- Customer / vehicle: the call's own association, never a guess ----------
  const linkUsable = !!call?.linked_customer_id
    && (call.link_basis === 'person' || (typeof call.routing_confidence === 'number' && call.routing_confidence >= CALL_LINK_MIN_CONFIDENCE));
  const customerId = linkUsable ? call!.linked_customer_id : null;
  const vehicleId = linkUsable ? call!.linked_vehicle_id ?? null : null;
  if (customerId) {
    notes.push({ field: 'customer', filled: true, text: call!.link_basis === 'person'
      ? 'Customer: the customer a team member linked this call to.'
      : 'Customer: the customer this call was matched to by the caller\'s number.' });
  } else if (call?.linked_customer_id) {
    notes.push({ field: 'customer', filled: false, text: 'Customer not filled: the call\'s customer match is uncertain — find the customer or enter them.' });
  } else {
    notes.push({ field: 'customer', filled: false, text: 'Customer not filled: the call is not linked to a customer — find the customer or enter them.' });
  }

  // ---------- Vehicle: linked vehicle first, else an unambiguous, confident hint ----------
  let vehicle: CallProposalPrefill['vehicle'] = null;
  if (vehicleId) {
    notes.push({ field: 'vehicle', filled: true, text: 'Vehicle: the vehicle this call is linked to.' });
  } else if (proposal.vehicle_hint) {
    const parsed = parseVehicleHint(proposal.vehicle_hint, opts.maxVehicleYear);
    if (parsed && confident) {
      vehicle = parsed;
      notes.push({ field: 'vehicle', filled: true, text: `Vehicle heard on the call: "${proposal.vehicle_hint}" (${tierText}).` });
    } else {
      notes.push({ field: 'vehicle', filled: false, text: parsed
        ? `Vehicle not filled: "${proposal.vehicle_hint}" was heard with ${tierText} — check it with the customer.`
        : `Vehicle not filled: "${proposal.vehicle_hint}" is not a clear year, make and model.` });
    }
  } else {
    notes.push({ field: 'vehicle', filled: false, text: 'Vehicle not filled: no vehicle was heard on the call.' });
  }

  // ---------- Day / window ----------
  let appointmentDate: string | null = null;
  if (proposal.proposed_date && ISO_DAY.test(proposal.proposed_date)) {
    if (!confident) {
      notes.push({ field: 'day', filled: false, text: `Day not set: ${proposal.proposed_date} was heard with ${tierText}.` });
    } else if (proposal.proposed_date < opts.todayInShop) {
      notes.push({ field: 'day', filled: false, text: `Day not set: the day heard (${proposal.proposed_date}) has already passed.` });
    } else {
      appointmentDate = proposal.proposed_date;
      notes.push({ field: 'day', filled: true, text: `Appointment day ${proposal.proposed_date}, heard on the call (${tierText}).` });
    }
  } else {
    notes.push({ field: 'day', filled: false, text: 'Day not set: no day was heard on the call.' });
  }
  if (proposal.proposed_window) {
    notes.push({ field: 'window', filled: false, text: `Time heard: "${proposal.proposed_window}" — not filled (agree the drop-off time with the customer).` });
  }

  // ---------- Service ----------
  const summary = proposal.service_summary.trim();
  const concern = confident ? summary : '';
  const requestedServices = confident && summary ? [summary] : [];
  notes.push(confident
    ? { field: 'service', filled: true, text: `Concern from the call (${tierText}) — edit it as needed.` }
    : { field: 'service', filled: false, text: `Concern not filled: "${summary}" was heard with ${tierText}.` });

  return { proposalId: proposal.id, tier, concern, requestedServices, appointmentDate, customerId, vehicleId, vehicle, notes };
}

/**
 * The field marks a call prefill shows (CORE-Y): the vehicle fields it filled carry the call's confidence; the ones
 * it could not fill read as "not found in the conversation". The customer comes from the call's link, not from the
 * extraction, and is said in the prefill card instead.
 */
export function callProposalProvenance(prefill: CallProposalPrefill): ExtractionProvenance {
  const t = prefill.tier;
  const v = prefill.vehicle;
  const fields: ExtractionProvenance['fields'] = {};
  if (!prefill.vehicleId) {
    fields['vehicle.year'] = v ? { state: 'extracted', confidence: t, value: String(v.year) } : { state: 'not_found', confidence: null, value: null };
    fields['vehicle.make'] = v ? { state: 'extracted', confidence: t, value: v.make } : { state: 'not_found', confidence: null, value: null };
    fields['vehicle.model'] = v ? { state: 'extracted', confidence: t, value: v.model } : { state: 'not_found', confidence: null, value: null };
  }
  if (!prefill.customerId) fields.customerName = { state: 'not_found', confidence: null, value: null };
  return { origin: 'conversation', fields };
}

/** A server refusal of a from-call ticket, in words the advisor can act on; null for any other error. */
export function callProposalRefusalMessage(err: unknown): string | null {
  const e = err as { message?: unknown; details?: unknown } | null | undefined;
  const text = `${typeof e?.message === 'string' ? e.message : ''} ${typeof e?.details === 'string' ? e.details : ''}`;
  if (/CALL_PROPOSAL_STALE/.test(text)) return 'A newer suggestion from the same call replaced this one. No ticket was created.';
  if (/CALL_PROPOSAL_ALREADY_DECIDED/.test(text)) return 'This call suggestion was already decided (confirmed on another ticket or rejected). No ticket was created.';
  if (/CALL_PROPOSAL_NOT_IN_SHOP/.test(text)) return 'This call suggestion is not a suggestion of this shop. No ticket was created.';
  if (/CALL_PROPOSAL_FORBIDDEN/.test(text)) return 'Only the front office can create a ticket from a call suggestion. No ticket was created.';
  return null;
}

// ─── Loading (through the caller's client; methods are always called on it, never detached) ───

interface Q {
  select: (cols: string) => Q;
  eq: (col: string, v: string) => Q;
  order: (col: string, o: { ascending: boolean }) => Q;
  limit: (n: number) => Q;
  maybeSingle: () => Promise<{ data: unknown; error: unknown }>;
  then: Promise<{ data: unknown; error: unknown }>['then'];
}
interface ClientLike { from: (table: string) => unknown }

export interface LoadedCallProposal {
  availability: CallProposalAvailability;
  proposal: CallProposalForTicket | null;
  call: CallProposalCallLink | null;
  customer: { id: string; full_name: string } | null;
}

const PROPOSAL_COLS = 'id, shop_id, call_event_id, service_summary, proposed_date, proposed_window, vehicle_hint, confidence, status, confirmed_ticket_id, created_at';

/**
 * Read the proposal (front-office RLS; scoped to the ACTIVE shop, so another shop's id reads as not found), whether a
 * later extraction of the same call replaced it, the call's association and the linked customer's name. A read
 * failure is "not found" — never a guess.
 */
export async function loadCallProposalForTicket(client: ClientLike, shopId: string, proposalId: string): Promise<LoadedCallProposal> {
  const nothing: LoadedCallProposal = { availability: { kind: 'not_found' }, proposal: null, call: null, customer: null };
  try {
    const t = (name: string) => client.from(name) as Q;
    const { data: p, error } = await t('call_appointment_proposals').select(PROPOSAL_COLS)
      .eq('id', proposalId).eq('shop_id', shopId).maybeSingle();
    if (error || !p) return nothing;
    const proposal = p as CallProposalForTicket;
    const { data: latest } = await t('call_appointment_proposals').select('id')
      .eq('call_event_id', proposal.call_event_id)
      .order('created_at', { ascending: false }).order('id', { ascending: false }).limit(1);
    const latestId = ((latest ?? []) as Array<{ id: string }>)[0]?.id ?? null;
    const availability = callProposalAvailability(proposal, latestId);
    const { data: c } = await t('call_events').select('linked_customer_id, linked_vehicle_id, link_basis, routing_confidence')
      .eq('id', proposal.call_event_id).eq('shop_id', shopId).maybeSingle();
    const call = (c ?? null) as CallProposalCallLink | null;
    let customer: LoadedCallProposal['customer'] = null;
    if (call?.linked_customer_id) {
      const { data: cu } = await t('customers').select('id, full_name').eq('id', call.linked_customer_id).eq('shop_id', shopId).maybeSingle();
      customer = (cu ?? null) as LoadedCallProposal['customer'];
    }
    return { availability, proposal, call, customer };
  } catch {
    return nothing;
  }
}
